import { onCleanup, onMount } from 'solid-js';
import { siteConfig } from '~/data/config';

type MetaSpec = { name?: string; property?: string; content: string };

function setMeta(spec: MetaSpec) {
  const selector = spec.name
    ? `meta[name="${spec.name}"]`
    : `meta[property="${spec.property}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    if (spec.name) el.setAttribute('name', spec.name);
    if (spec.property) el.setAttribute('property', spec.property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', spec.content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export interface SeoOptions {
  title: string;
  description: string;
  path?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
}

export function applySeo(opts: SeoOptions) {
  const url = `${siteConfig.domain}${opts.path ?? '/'}`;
  const image = opts.ogImage ?? siteConfig.ogImage;

  document.title = opts.title;
  setMeta({ name: 'description', content: opts.description });
  setLink('canonical', url);

  setMeta({ property: 'og:title', content: opts.title });
  setMeta({ property: 'og:description', content: opts.description });
  setMeta({ property: 'og:url', content: url });
  setMeta({ property: 'og:image', content: image });
  setMeta({ property: 'og:type', content: opts.ogType ?? 'website' });

  setMeta({ name: 'twitter:title', content: opts.title });
  setMeta({ name: 'twitter:description', content: opts.description });
  setMeta({ name: 'twitter:image', content: image });
}

export function useSeo(getOpts: () => SeoOptions) {
  onMount(() => {
    applySeo(getOpts());
  });
}

export function injectJsonLd(id: string, data: unknown) {
  onMount(() => {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    onCleanup(() => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });
  });
}
